/**
 * Manage ▸ **Automations** (captures `15-after-create.png`, `62-card-menu.png`,
 * `63-after-cleanup.png`).
 *
 * One card per automation: the rendered sentence, then a meta row — importance chip, `Updated
 * <relative>`, `Owner <avatar>`, `Description / Add description` — with the on/off toggle and a
 * `…` menu on the right. A card whose automation is parked is greyed and carries its notice
 * (R5/R7); switching such a card on is refused by the api with that same notice, which is
 * surfaced here rather than swallowed.
 */
import { Avatar, Icon, RelativeTime, Search, Toggle, icons } from '@elizmonday/ui';
import { useRef, useState } from 'react';
import { PopoverOption, SlotPopover } from './slots/SlotPopover';
import { DEFAULT_IMPORTANCE, FILTER_IMPORTANCE, FILTER_STATUSES, noticeText } from './notices';
import { AutomationSentence, type SentenceChunk } from './sentence';
import type { AutomationSummaryView, PersonView } from './types';

export interface AutomationFilters {
  search: string;
  status: string | null;
  importance: string | null;
  ownerId: string | null;
  trigger: string | null;
}

export const EMPTY_FILTERS: AutomationFilters = { search: '', status: null, importance: null, ownerId: null, trigger: null };

export function filtersAreEmpty(filters: AutomationFilters): boolean {
  return (
    filters.search.trim().length === 0 &&
    filters.status === null &&
    filters.importance === null &&
    filters.ownerId === null &&
    filters.trigger === null
  );
}

/** Client-side because the panel filters what is already on screen (capture 62). */
export function applyFilters(rows: readonly AutomationSummaryView[], filters: AutomationFilters): AutomationSummaryView[] {
  const needle = filters.search.trim().toLowerCase();
  return rows.filter((row) => {
    if (needle.length > 0 && !row.title.toLowerCase().includes(needle)) return false;
    if (filters.status !== null) {
      const status = row.state === 'error' ? 'error' : row.runnable ? 'active' : 'deactivated';
      if (status !== filters.status) return false;
    }
    if (filters.importance !== null && filters.importance !== DEFAULT_IMPORTANCE) return false;
    if (filters.ownerId !== null && row.ownerId !== filters.ownerId) return false;
    if (filters.trigger !== null && !row.title.toLowerCase().startsWith(filters.trigger.toLowerCase())) return false;
    return true;
  });
}

/** The Trigger column of the Filter panel: the distinct opening clauses actually in the list. */
export function triggerFacets(rows: readonly AutomationSummaryView[]): string[] {
  const facets = new Set<string>();
  for (const row of rows) {
    const words = row.title.split(/\s+/).slice(0, 4).join(' ');
    if (words.length > 0) facets.add(words);
  }
  return [...facets].slice(0, 8);
}

export interface AutomationListProps {
  rows: AutomationSummaryView[];
  filters: AutomationFilters;
  onFiltersChange(next: AutomationFilters): void;
  owners: PersonView[];
  onToggle(row: AutomationSummaryView, active: boolean): void;
  onEdit(row: AutomationSummaryView): void;
  onDuplicate(row: AutomationSummaryView): void;
  onDelete(row: AutomationSummaryView): void;
  onExploreTemplates(): void;
  compact?: boolean;
  onCompactChange?: (compact: boolean) => void;
  error?: string | null;
  /**
   * T-8d: the chunked sentence of the rows the centre had to assemble itself (an imported
   * automation with an empty stored title). Where there is one, the card prints monday's
   * card text with the **configured values in bold**; where there is not, the stored title.
   */
  sentences?: ReadonlyMap<string, SentenceChunk[]>;
}

export function AutomationList({
  rows,
  filters,
  onFiltersChange,
  owners,
  onToggle,
  onEdit,
  onDuplicate,
  onDelete,
  onExploreTemplates,
  compact = false,
  onCompactChange,
  error = null,
  sentences,
}: AutomationListProps) {
  const [filterOpen, setFilterOpen] = useState(false);
  const filterAnchor = useRef<HTMLButtonElement>(null);
  const visible = applyFilters(rows, filters);

  return (
    <div className="ez-automation-manage__panel" data-testid="automations-list-panel">
      <div className="ez-automation-toolbar">
        <div className="ez-automation-toolbar__search">
          <Search
            value={filters.search}
            onChange={(value) => onFiltersChange({ ...filters, search: value })}
            placeholder="Search"
            size="small"
            aria-label="Search automations"
            data-testid="automations-search"
          />
        </div>
        <button
          ref={filterAnchor}
          type="button"
          className="ez-automation-toolbar__filter"
          aria-expanded={filterOpen}
          aria-haspopup="dialog"
          data-active={!filtersAreEmpty(filters)}
          data-testid="automations-filter-button"
          onClick={() => setFilterOpen((open) => !open)}
        >
          <Icon icon={icons.filter} size={16} ignoreFocusStyle />
          Filter
        </button>
        <span className="ez-automation-toolbar__spacer" />
        <button
          type="button"
          className="ez-automation-toolbar__icon"
          aria-label="Export automations"
          data-testid="automations-export"
          onClick={() => exportAutomations(visible)}
        >
          <Icon icon={icons.upload} size={16} ignoreFocusStyle />
        </button>
        <div className="ez-automation-viewswitch" role="group" aria-label="List density">
          <button
            type="button"
            aria-pressed={!compact}
            aria-label="Card view"
            data-testid="automations-view-cards"
            onClick={() => onCompactChange?.(false)}
          >
            <Icon icon={icons.board} size={16} ignoreFocusStyle />
          </button>
          <button
            type="button"
            aria-pressed={compact}
            aria-label="Compact view"
            data-testid="automations-view-compact"
            onClick={() => onCompactChange?.(true)}
          >
            <Icon icon={icons.lines} size={16} ignoreFocusStyle />
          </button>
        </div>
      </div>

      {error === null ? null : (
        <div className="ez-automation-error" role="alert" data-testid="automations-list-error">
          {error}
        </div>
      )}

      {visible.length === 0 ? (
        <div className="ez-automation-empty" data-testid="automations-empty">
          <p className="ez-automation-empty__title">Create your first automation to save time</p>
          <p className="ez-automation-empty__body">You can also explore pre-built automation templates.</p>
          <button type="button" className="ez-automation-secondary" onClick={onExploreTemplates} data-testid="automations-explore">
            Explore templates
          </button>
        </div>
      ) : (
        <ul className="ez-automation-cards" data-compact={compact} data-testid="automations-cards">
          {visible.map((row) => (
            <AutomationCard
              key={row.id}
              row={row}
              sentence={sentences?.get(row.id)}
              onToggle={onToggle}
              onEdit={onEdit}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
            />
          ))}
        </ul>
      )}

      {filterOpen ? (
        <SlotPopover
          anchorRef={filterAnchor}
          label="Filters"
          width={952}
          onDismiss={() => setFilterOpen(false)}
          testId="automations-filter-panel"
        >
          <FilterPanel rows={rows} filters={filters} owners={owners} onChange={onFiltersChange} />
        </SlotPopover>
      ) : null}
    </div>
  );
}

function AutomationCard({
  row,
  sentence,
  onToggle,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  row: AutomationSummaryView;
  sentence?: readonly SentenceChunk[];
  onToggle(row: AutomationSummaryView, active: boolean): void;
  onEdit(row: AutomationSummaryView): void;
  onDuplicate(row: AutomationSummaryView): void;
  onDelete(row: AutomationSummaryView): void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuAnchor = useRef<HTMLButtonElement>(null);
  const notice = noticeText(row.noticeMessage);
  const stats = (row.stats ?? {}) as Record<string, number | undefined>;
  const runs = Number(stats.runs ?? 0);

  return (
    <li className="ez-automation-card" data-runnable={row.runnable} data-testid={`automation-card-${row.id}`}>
      <div className="ez-automation-card__main">
        <p className="ez-automation-card__title" data-testid={`automation-title-${row.id}`}>
          {sentence === undefined ? row.title : <AutomationSentence chunks={sentence} />}
        </p>
        <div className="ez-automation-card__meta">
          <span className="ez-automation-card__importance">
            <Icon icon={icons.activity} size={14} ignoreFocusStyle />
            {DEFAULT_IMPORTANCE}
          </span>
          <span className="ez-automation-card__meta-item">
            Updated <RelativeTime value={row.updatedAt} />
          </span>
          <span className="ez-automation-card__meta-item">
            Owner
            {row.owner === null ? (
              <span className="ez-automation-card__owner-empty">—</span>
            ) : (
              <Avatar text={initials(row.owner.name)} type="text" size="small" aria-label={row.owner.name} />
            )}
          </span>
          <span className="ez-automation-card__meta-item">
            Description <span className="ez-automation-card__description">{row.description ?? 'Add description'}</span>
          </span>
          {runs === 0 ? null : (
            /* Ours, not monday's: the run counters the Run history tab aggregates, in one
               glance (`specs/M6-A2` §7, deviation D2). */
            <span className="ez-automation-card__meta-item" data-testid={`automation-runs-${row.id}`}>
              {`${String(runs)} runs`}
              {Number(stats.failed ?? 0) > 0 ? ` · ${String(stats.failed)} failed` : ''}
            </span>
          )}
        </div>
        {notice === null ? null : (
          <p className="ez-automation-card__notice" role="status" data-testid={`automation-notice-${row.id}`}>
            {notice}
          </p>
        )}
      </div>

      <div className="ez-automation-card__side">
        {/* The toggle is wrapped so the tests (and the hit-test) have a stable target: Vibe
            renders the input itself with its own markup. */}
        <span className="ez-automation-card__toggle" data-testid={`automation-toggle-${row.id}`}>
          <Toggle
            // monday's card shows the pill alone; Vibe's default "Off / On" labels are not
            // in any capture (`63-after-cleanup.png`).
            areLabelsHidden
            isSelected={row.runnable}
            onChange={(checked: boolean) => onToggle(row, checked)}
            aria-label={`Turn ${row.runnable ? 'off' : 'on'} ${row.title}`}
            // P-204: the wrapper above is a hit target, this is the control. Vibe forwards
            // `data-testid` to the `<input>` it renders, so the id lands on the thing that is
            // actually toggled — which is what the coverage ledger counts.
            data-testid={`automation-toggle-input-${row.id}`}
          />
        </span>
        <button
          ref={menuAnchor}
          type="button"
          className="ez-automation-card__menu"
          aria-label={`Options for ${row.title}`}
          aria-haspopup="menu"
          data-testid={`automation-menu-${row.id}`}
          onClick={() => setMenuOpen((open) => !open)}
        >
          {/* Both names now draw Vibe 4.1's `⋯` — see the icon registry's note (T-8d). */}
          <Icon icon={icons.moreActions} size={16} ignoreFocusStyle />
        </button>
      </div>

      {menuOpen ? (
        <SlotPopover
          anchorRef={menuAnchor}
          label={`Options for ${row.title}`}
          width={228}
          onDismiss={() => setMenuOpen(false)}
          testId={`automation-menu-popover-${row.id}`}
        >
          <PopoverOption
            testId={`automation-menu-edit-${row.id}`}
            onClick={() => {
              setMenuOpen(false);
              onEdit(row);
            }}
          >
            Edit automation
          </PopoverOption>
          <PopoverOption
            testId={`automation-menu-duplicate-${row.id}`}
            onClick={() => {
              setMenuOpen(false);
              onDuplicate(row);
            }}
          >
            Duplicate
          </PopoverOption>
          <PopoverOption
            testId={`automation-menu-delete-${row.id}`}
            onClick={() => {
              setMenuOpen(false);
              onDelete(row);
            }}
          >
            Delete
          </PopoverOption>
        </SlotPopover>
      ) : null}
    </li>
  );
}

export function FilterPanel({
  rows,
  filters,
  owners,
  onChange,
}: {
  rows: readonly AutomationSummaryView[];
  filters: AutomationFilters;
  owners: PersonView[];
  onChange(next: AutomationFilters): void;
}) {
  const triggers = triggerFacets(rows);
  const toggle = <K extends keyof AutomationFilters>(key: K, value: AutomationFilters[K]): void =>
    onChange({ ...filters, [key]: filters[key] === value ? null : value });

  return (
    <div className="ez-automation-filters" data-testid="automations-filters">
      <div className="ez-automation-filters__head">
        <span className="ez-automation-filters__title">Filters</span>
        <button
          type="button"
          className="ez-automation-link"
          data-testid="automations-filters-clear"
          onClick={() => onChange({ ...EMPTY_FILTERS, search: filters.search })}
        >
          Clear All
        </button>
      </div>
      <div className="ez-automation-filters__columns">
        <section>
          <h4>Status</h4>
          {FILTER_STATUSES.map((status) => (
            <button
              key={status.value}
              type="button"
              className="ez-automation-filters__chip"
              aria-pressed={filters.status === status.value}
              data-testid={`automations-filter-status-${status.value}`}
              onClick={() => toggle('status', status.value)}
            >
              <span className="ez-automation-dot" style={{ background: status.color }} aria-hidden="true" />
              {status.text}
            </button>
          ))}
        </section>
        <section>
          <h4>Importance</h4>
          {FILTER_IMPORTANCE.map((importance) => (
            <button
              key={importance}
              type="button"
              className="ez-automation-filters__chip"
              aria-pressed={filters.importance === importance}
              data-testid={`automations-filter-importance-${importance.toLowerCase()}`}
              onClick={() => toggle('importance', importance)}
            >
              <Icon icon={icons.activity} size={14} ignoreFocusStyle />
              {importance}
            </button>
          ))}
        </section>
        <section>
          <h4>Owner</h4>
          {owners.map((owner) => (
            <button
              key={owner.id}
              type="button"
              className="ez-automation-filters__chip"
              aria-pressed={filters.ownerId === owner.id}
              data-testid={`automations-filter-owner-${owner.id}`}
              onClick={() => toggle('ownerId', owner.id)}
            >
              <Avatar text={initials(owner.name)} type="text" size="small" aria-label={owner.name} />
              {owner.name}
            </button>
          ))}
        </section>
        <section>
          <h4>Trigger</h4>
          {triggers.map((trigger) => (
            <button
              key={trigger}
              type="button"
              className="ez-automation-filters__chip ez-automation-filters__chip--wide"
              aria-pressed={filters.trigger === trigger}
              title={trigger}
              data-testid="automations-filter-trigger"
              onClick={() => toggle('trigger', trigger)}
            >
              {trigger}
            </button>
          ))}
        </section>
      </div>
    </div>
  );
}

/** The toolbar's export icon: the list as JSON, which is what an admin can re-import. */
function exportAutomations(rows: readonly AutomationSummaryView[]): void {
  const payload = JSON.stringify(
    rows.map((row) => ({ id: row.id, title: row.title, active: row.active, state: row.state, owner: row.owner?.name ?? null })),
    null,
    2,
  );
  const blob = new Blob([payload], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'automations.json';
  anchor.click();
  URL.revokeObjectURL(url);
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}
